import { Component, OnInit, Input, EventEmitter, Output, ViewChild } from '@angular/core';
import { InventoryAsset } from '@app/_models/_DTOs/inventoryAsset';
import { InventoryAssetService } from '@app/_services/inventoryAsset.service';
import { ITuple } from '@app/_models/tuple';
import { AssetType } from '@app/_models/enums/assetType';
import { AssetTypeName, Asset } from '@app/_models/_DTOs/asset';
import { SCREEN_SIZE } from '@app/_helpers/screen-size.enum';
import { ResizeService } from '@app/_services/resize.service';
import { AssetDetailsComponent } from '@app/assets/asset-details/asset-details.component';
import { AssetGroupService } from '@app/_services/assetGroup.service';
import { EmployeeService } from '@app/_services/employee.service';
import { SupplierService } from '@app/_services/supplier.service';
import { DepartmentService } from '@app/_services/department.service';
import { LocationService } from '@app/_services/location.service';
import { AssetGroup } from '@app/_models/_DTOs/assetGroup';
import { Department } from '@app/_models/_DTOs/department';
import { Supplier } from '@app/_models/_DTOs/supplier';
import { Employee } from '@app/_models/_DTOs/employee';
import { Location } from '@app/_models/_DTOs/location';
import { Sort } from '@app/_models/sort';
import { SortDirection } from '@app/_models/enums/sortDirection';

@Component({
  selector: 'app-inventory-assets',
  templateUrl: './inventory-assets.component.html',
  styleUrls: ['./inventory-assets.component.css']
})
export class InventoryAssetsComponent implements OnInit {

  @Input() inventoryId: number;
  @Input() isOpen: boolean;
  @Output() drawerClosed = new EventEmitter();
  @Output() inventoryAssetSelected: EventEmitter<InventoryAsset> = new EventEmitter();

  drawerWidth: string;

  inventoryAssets: InventoryAsset[];

  @ViewChild(AssetDetailsComponent, { static: false })
  private assetDetailsComponent: AssetDetailsComponent;

  pageIndex = 1;
  pageSize = 10;
  total = 1;
  loading = true;

  selectedSorts: Sort[] = [];

  showOnlyAssignedAssets: boolean = true;
  nameSearchValue = "";
  inventoryCodeSearchValue = "";
  barCodeSearchValue = "";
  employeeIdSearchValue: number[] = [];
  locationIdSearchValue: number[] = [];
  departmentIdSearchValue: number;
  assetGroupIdSearchValue: number;

  searchPanelActive: boolean = false;

  locations: Location[] = [];
  assetGroups: AssetGroup[] = [];
  departments: Department[] = [];
  suppliers: Supplier[] = [];
  employees: Employee[] = [];

  constructor(
    private inventoryAssetService: InventoryAssetService,
    private resizeService: ResizeService,
    private locationService: LocationService,
    private departmentService: DepartmentService,
    private supplierService: SupplierService,
    private employeeService: EmployeeService,
    private assetGroupService: AssetGroupService) {
    this.resizeService.onResize$.subscribe(size => {
      this.setDrawerWidth(size);
    });
  }

  ngOnInit() {
    if (this.resizeService.currentSize != null) {
      this.setDrawerWidth(this.resizeService.currentSize);
    }
    // this.searchData(true);
  }

  selectInventoryAsset(inventoryAsset: InventoryAsset) {
    this.inventoryAssetSelected.emit(inventoryAsset);
    this.closeList();
  }

  closeList() {
    this.inventoryAssets = null;
    this.isOpen = false;
    this.drawerClosed.emit();
  }

  sort(sort: { key: string; value: string }): void {
    var newSort: Sort = {
      PropertyName: sort.key,
      Direction: null
    };
    if (sort.value) {
      if (sort.value == "ascend") {
        newSort.Direction = SortDirection.Ascending;
      }
      else {
        newSort.Direction = SortDirection.Descending;
      }
    }

    if (newSort.Direction == null) {
      var existingSort = this.selectedSorts.find(x => x.PropertyName == newSort.PropertyName);
      if (existingSort) {
        const index = this.selectedSorts.indexOf(existingSort);
        if (index > -1) {
          this.selectedSorts.splice(index, 1);
        }
      }
    }
    else {
      var existingSort = this.selectedSorts.find(x => x.PropertyName == newSort.PropertyName);
      if (existingSort) {
        existingSort.Direction = newSort.Direction;
      }
      else {
        this.selectedSorts.push(newSort);
      }
    }
    this.searchData();
  }

  getSortString() {
    var sortString = "";
    if (this.selectedSorts && this.selectedSorts.length > 0) {
      for (let sort of this.selectedSorts) {
        if (sort.Direction == SortDirection.Ascending) {
          if (sortString.length == 0) {
            sortString = sort.PropertyName;
          }
          else {
            sortString += "," + sort.PropertyName;
          }
        }
        else {
          if (sortString.length == 0) {
            sortString = "-" + sort.PropertyName;
          }
          else {
            sortString += ",-" + sort.PropertyName;
          }
        }
      }
    }
    return sortString;
  }

  searchData(reset: boolean = false): void {
    if (this.inventoryId != null) {
      if (reset) {
        this.pageIndex = 1;
      }
      var filters = "InventoryId==" + this.inventoryId + ",";
      if (this.nameSearchValue.length > 0) {
        filters += "Asset.Name@=" + this.nameSearchValue + ",";
      }
      if (this.inventoryCodeSearchValue.length > 0) {
        filters +=
          "InventoryAssetInventoryNumber@=" +
          this.inventoryCodeSearchValue +
          ",";
      }
      if (this.barCodeSearchValue.length > 0) {
        filters += "Asset.BarCode@=" + this.barCodeSearchValue + ",";
      }
      if (this.employeeIdSearchValue.length > 0) {
        filters += "Asset.EmployeeId==" + this.employeeIdSearchValue.join("|") + ",";
      }
      if (this.locationIdSearchValue.length > 0) {
        filters += "Asset.LocationId==" + this.locationIdSearchValue.join("|") + ",";
      }
      if (this.departmentIdSearchValue) {
        filters += "Asset.DepartmentId==" + this.departmentIdSearchValue.toString() + ",";
      }
      if (this.assetGroupIdSearchValue) {
        filters += "Asset.GroupId==" + this.assetGroupIdSearchValue.toString() + ",";
      }

      var sortString = this.getSortString();
      if (sortString.length == 0) {
        sortString = "-Created"
      }
      else {
        sortString += ",-Created"
      }

      this.loading = true;
      if (this.showOnlyAssignedAssets) {
        this.inventoryAssetService
          .getPageForUser(this.pageIndex, this.pageSize, sortString, filters)
          .subscribe((data: ITuple<InventoryAsset>) => {
            this.loading = false;
            this.total = data.item2;
            this.inventoryAssets = data.item1;
          });
      }
      else {
        this.inventoryAssetService
          .getPage(this.pageIndex, this.pageSize, sortString, filters)
          .subscribe((data: ITuple<InventoryAsset>) => {
            this.loading = false;
            this.total = data.item2;
            this.inventoryAssets = data.item1;
          });
      }
    }
  }

  reset(): void {
    this.nameSearchValue = '';
    this.inventoryCodeSearchValue = '';
    this.barCodeSearchValue = '';
    this.searchData(true);
  }

  openDetails(asset: Asset) {
    this.assetDetailsComponent.loadAsset(asset.id);
  }

  closeDetails() {
    this.assetDetailsComponent.selectedAsset = null;
    this.assetDetailsComponent.isOpen = false;
  }

  private getAssetTypeName(assetType: AssetType) {
    return AssetTypeName.get(assetType);
  }

  setDrawerWidth(size: SCREEN_SIZE) {
    switch (size) {
      case SCREEN_SIZE.XL: {
        this.drawerWidth = "calc(60vw - 256px)";
        break;
      }
      case SCREEN_SIZE.LG: {
        this.drawerWidth = "calc(60vw - 256px)";
        break;
      }
      case SCREEN_SIZE.MD: {
        this.drawerWidth = "calc(100vw - 256px)";
        break;
      }
      default: {
        this.drawerWidth = "100vw";
        break;
      }
    }
  }

  loadDataForDropdowns() {
    this.locationService.getAllBasic().subscribe((data: Location[]) => {
      this.locations = data;
      // this.filteredLocations = data;
    });
    this.employeeService.getAllBasic().subscribe((data: Employee[]) => {
      this.employees = data;
    });
    this.departmentService.getAllBasic().subscribe((data: Department[]) => {
      this.departments = data;
    });
    this.supplierService.getAllBasic().subscribe((data: Supplier[]) => {
      this.suppliers = data;
    });
    this.assetGroupService.getAllBasic().subscribe((data: AssetGroup[]) => {
      this.assetGroups = data;
    });
  }

}
